/**
 * filterTree.js
 *
 * LGX-653: entity-agnostic nested AND/OR filter-tree utilities, extracted from the pattern
 * already proven in Shipment/TabContent/shipmentShared.js (RULE_FIELDS + rules[]/matchType).
 * Every function here takes the caller's own `ruleFields` config array as a parameter instead
 * of importing a hardcoded catalog, so the same tree/UI machinery works for Orders (and any
 * future grid) without duplicating this logic per entity.
 *
 * A `ruleFields` entry: { key, label, type: "select"|"text"|"date"|"number", group, ops,
 * backendField, options? (for select), placeholder? }.
 *
 * Tree shape (same as Shipment's):
 *  - leaf:  { id, field, op, value }        -- value is an array only when op === "IN"
 *  - group: { id, matchType: "AND"|"OR", children: Node[] }
 */

export const MAX_RULE_DEPTH = 3;

const OPERATOR_LABELS = {
  EQ: "equals",
  NEQ: "does not equal",
  IN: "is any of",
  CONTAINS: "contains",
  STARTS_WITH: "starts with",
  GTE: "is on/after or ≥",
  LTE: "is on/before or ≤",
};

export const operatorLabel = (op) => OPERATOR_LABELS[op] ?? op;

export const ruleFieldByKey = (ruleFields, key) => ruleFields.find((f) => f.key === key);

export const ruleFieldGroups = (ruleFields) =>
  ruleFields.reduce((groups, field) => {
    if (field.hidden) return groups;
    const name = field.group || "Other";
    const existing = groups.find((g) => g.name === name);
    if (existing) existing.fields.push(field);
    else groups.push({ name, fields: [field] });
    return groups;
  }, []);

export const makeRuleId = () =>
  `rule-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

export const makeRule = (ruleFields, fieldKey, overrides = {}) => {
  const field = ruleFieldByKey(ruleFields, fieldKey) ?? ruleFields[0];
  const op = overrides.op ?? field.ops[0];
  return {
    id: makeRuleId(),
    field: field.key,
    op,
    value: op === "IN" ? [] : "",
    ...overrides,
  };
};

export const isRuleComplete = (ruleFields, rule) => {
  const field = ruleFieldByKey(ruleFields, rule?.field);
  if (!field || !field.ops.includes(rule.op)) return false;
  if (Array.isArray(rule.value)) return rule.value.length > 0;
  return String(rule.value ?? "").trim().length > 0;
};

export const describeRule = (ruleFields, rule) => {
  const field = ruleFieldByKey(ruleFields, rule?.field);
  if (!field) return "";
  const value = Array.isArray(rule.value) ? rule.value.join(", ") : rule.value;
  return `${field.label} ${operatorLabel(rule.op)} ${value}`;
};

// ── Nested groups ────────────────────────────────────────────────────────────

export const isRuleGroup = (node) => Array.isArray(node?.children);

export const makeRuleGroup = (matchType = "OR", children = []) => ({
  id: `group-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
  matchType: matchType === "OR" ? "OR" : "AND",
  children,
});

export const ruleMatchType = (f) => (f?.matchType === "OR" ? "OR" : "AND");

export const isNodeComplete = (ruleFields, node) =>
  isRuleGroup(node)
    ? node.children.some((c) => isNodeComplete(ruleFields, c))
    : isRuleComplete(ruleFields, node);

export const pruneRuleNodes = (ruleFields, nodes = []) =>
  nodes.reduce((kept, node) => {
    if (!isRuleGroup(node)) {
      if (isRuleComplete(ruleFields, node)) kept.push(node);
      return kept;
    }
    const children = pruneRuleNodes(ruleFields, node.children);
    if (children.length) kept.push({ ...node, children });
    return kept;
  }, []);

export const flattenRuleLeaves = (nodes = []) =>
  nodes.flatMap((node) => (isRuleGroup(node) ? flattenRuleLeaves(node.children) : [node]));

export const countRuleNodes = (nodes = []) => flattenRuleLeaves(nodes).length;

export const describeNode = (ruleFields, node) => {
  if (!isRuleGroup(node)) return describeRule(ruleFields, node);
  const joiner = ruleMatchType(node) === "OR" ? " or " : " and ";
  const inner = node.children
    .filter((c) => isNodeComplete(ruleFields, c))
    .map((c) => describeNode(ruleFields, c))
    .join(joiner);
  return node.children.filter((c) => isNodeComplete(ruleFields, c)).length > 1 ? `(${inner})` : inner;
};

// Immutable tree edits, keyed by node id.

export const updateRuleNode = (nodes = [], id, updater) =>
  nodes.map((node) => {
    if (node.id === id) return updater(node);
    if (isRuleGroup(node)) return { ...node, children: updateRuleNode(node.children, id, updater) };
    return node;
  });

export const removeRuleNode = (nodes = [], id) =>
  nodes
    .filter((node) => node.id !== id)
    .map((node) => (isRuleGroup(node) ? { ...node, children: removeRuleNode(node.children, id) } : node));

export const appendRuleNode = (nodes = [], groupId, child) => {
  if (!groupId) return [...nodes, child];
  return updateRuleNode(nodes, groupId, (group) =>
    isRuleGroup(group) ? { ...group, children: [...group.children, child] } : group
  );
};

// ── Tree -> backend FilterNode ────────────────────────────────────────────────

/** One rule leaf -> a backend FilterNode leaf ({field, op, value}), or null if incomplete. */
export const ruleToFilterNode = (ruleFields, rule) => {
  const field = ruleFieldByKey(ruleFields, rule?.field);
  if (!field || !isRuleComplete(ruleFields, rule)) return null;

  const values = (Array.isArray(rule.value) ? rule.value : [rule.value])
    .map((v) => String(v).trim())
    .filter(Boolean);
  if (!values.length) return null;

  const value = rule.op === "IN" ? values.join(",") : values[0];
  return { field: field.backendField, op: rule.op, value };
};

/** Any tree node (leaf or group) -> a backend FilterNode, or null if it prunes to nothing. */
export const nodeToFilterNode = (ruleFields, node) => {
  if (!isRuleGroup(node)) return ruleToFilterNode(ruleFields, node);
  const filters = node.children.map((c) => nodeToFilterNode(ruleFields, c)).filter(Boolean);
  if (!filters.length) return null;
  if (filters.length === 1) return filters[0];
  return { operator: ruleMatchType(node), filters };
};

/**
 * Builds the full backend FilterNode payload from { rules, matchType } advanced-filter state.
 * Returns null when there's nothing to send (caller should omit `filter` from the request body).
 */
export const buildFilterNode = (ruleFields, filters) => {
  const pruned = pruneRuleNodes(ruleFields, filters?.rules || []);
  const nodes = pruned.map((n) => nodeToFilterNode(ruleFields, n)).filter(Boolean);
  if (nodes.length === 0) return null;
  if (nodes.length === 1 && ruleMatchType(filters) !== "OR") return nodes[0];
  if (nodes.length === 1) return nodes[0];
  return { operator: ruleMatchType(filters), filters: nodes };
};

export const countActiveFilters = (ruleFields, filters) =>
  countRuleNodes(pruneRuleNodes(ruleFields, filters?.rules || []));

/** True when any leaf in the tree targets `fieldKey` (e.g. to avoid double-filtering status). */
export const treeTouchesField = (nodes = [], fieldKey) =>
  flattenRuleLeaves(nodes).some((r) => r.field === fieldKey);
